import classNames from 'classnames';
import React, {
  CSSProperties,
  Dispatch,
  ReactNode,
  SetStateAction,
} from 'react';
import { Collapse } from 'react-bootstrap';

import Icon from 'components/icons/Icon';
import { IconSize, IconSymbol, IconVariant } from 'components/icons/Icon.types';
import { evaluateThis } from 'helpers/helpers';

import './Collapsable.scss';

type Dimension = 'height' | 'width';

type CollapsableControllerProps = {
  open: boolean; // open state
  setOpen: Dispatch<SetStateAction<boolean>>; // state action that toggles the open state
  className?: string; // extra classname for the controller
  active?: boolean; // if true, renders top div with class 'active'
  hide?: boolean; // if true, renders controller with class 'hide' when opened
  icon?: IconSymbol | null; // name of the icon to be displayed on the controller
  closeIcon?: IconSymbol | null; // the name of the icon to use for closing
  iconSize?: number | 'large';
  iconVariant?: IconVariant; // variant (dark, light) of the icon
  iconIsTrigger?: boolean; // if true, only the icon will trigger open/close
  noPlus?: boolean; // if true, hides the plus and minus
  noIcon?: boolean; // if true, hides all icons and the symbol
  symbol?: number | string; // symbol to be displayed on the controller (only if there's no icon)
  onCollapse?: (open: boolean) => void; // callback to be called on collapse and uncollapse
  tooltip?:
    | string
    | React.JSX.Element
    | (() => string)
    | (() => React.JSX.Element)
    | null;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  style?: CSSProperties;
  height?: number; // height of the controller
  width?: number; // width of the controller
  children?: ReactNode;
};
function CollapsableController(props: CollapsableControllerProps) {
  const {
    open,
    setOpen,
    className,
    active,
    hide,
    icon,
    closeIcon,
    iconSize,
    iconVariant,
    iconIsTrigger,
    noPlus,
    noIcon,
    symbol,
    onCollapse,
    tooltip,
    disabled = false,
    onClick,
    style,
    height,
    width,
  } = props;
  const children = React.Children.toArray(props.children);
  const hasTooltip = Boolean(
    (icon || symbol || children.length > 0) && tooltip,
  );

  function wrapTooltipTrigger(content: ReactNode) {
    if (!hasTooltip) return content;
    return <div className="collapsable_tooltip_trigger">{content}</div>;
  }

  const onControllerClicked = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (disabled) return;
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
    if (onCollapse) onCollapse(!open);
  };

  return (
    <div
      key={1}
      className={classNames('collapsable_controller', className, {
        hide: hide && open,
        active: active,
        disabled: disabled,
      })}
      onClick={iconIsTrigger ? onClick : onControllerClicked}
      style={{ ...style, height: height, width: width }}
    >
      {noIcon ? null : (
        <>
          {noPlus ? null : (
            <Icon
              className="collapsable_icon"
              symbol={open ? IconSymbol.minus : IconSymbol.plus}
              variant={iconVariant}
              size={iconSize === 'large' ? undefined : iconSize}
              onClick={iconIsTrigger ? onControllerClicked : onClick}
            />
          )}
          {icon || symbol
            ? wrapTooltipTrigger(
                icon ? (
                  <div
                    className={classNames({
                      collapsable_symbol: iconSize === 'large',
                      collapsable_icon: iconSize !== 'large',
                    })}
                  >
                    <Icon
                      symbol={open ? (closeIcon ?? icon) : icon}
                      variant={iconVariant}
                      size={iconSize === 'large' ? IconSize.large : iconSize}
                      style={{ margin: 'auto' }}
                      onClick={iconIsTrigger ? onControllerClicked : onClick}
                    />
                  </div>
                ) : symbol ? ( // if there's no icon, there may be a symbol
                  <div className="collapsable_symbol">{symbol}</div>
                ) : null,
              )
            : null}
        </>
      )}
      {noIcon || (!icon && !symbol) ? (
        <>
          {wrapTooltipTrigger(children[0])}
          {children.slice(1)}
        </>
      ) : (
        children
      )}
      {hasTooltip ? (
        <div key={2} className="collapsable_tooltip">
          {evaluateThis(tooltip)}
        </div>
      ) : null}
    </div>
  );
}

type CollapsableContentProps = {
  open: boolean; // open state
  className?: string; // extra classname for the content
  dimension?: Dimension;
  children?: ReactNode;
  timeout?: number;
};
const CollapsableContent = React.forwardRef<
  HTMLDivElement,
  CollapsableContentProps
>((props, ref) => {
  const { open, className, dimension, children, timeout = 1300 } = props;

  return (
    <Collapse
      key={3}
      in={open}
      dimension={dimension || 'width'}
      // timeout={timeout}
      className={className}
    >
      <div ref={ref} className={'collapsable_content'}>
        {children}
      </div>
    </Collapse>
  );
});

type CollapsableProps = {
  className?: string; // extra class given to the top div (e.g., 'phase' or 'block')
  dimension?: Dimension;
  style?: React.CSSProperties;
  ref?: React.RefObject<HTMLDivElement>;
  children: ReactNode; // the controller and the content of the collapsable (that which collapses)
};
const CollapsableFC = React.forwardRef<HTMLDivElement, CollapsableProps>(
  (props, ref) => {
    const { className, dimension, style, children } = props;
    return (
      <div
        className={classNames(className, 'collapsable', dimension || 'width')}
        ref={ref}
        style={style}
      >
        {children}
      </div>
    );
  },
);

interface ICollapsableContainerProps {
  id?: string;
  className?: string;
  dimension?: Dimension;
  children: ReactNode;
}
export function CollapsableContainer(props: ICollapsableContainerProps) {
  const { id, className, dimension, children } = props;
  return (
    <div
      id={id}
      className={classNames(
        className,
        'collapsable_container',
        dimension || 'width',
      )}
    >
      {children}
    </div>
  );
}

const Collapsable = Object.assign(CollapsableFC, {
  Controller: CollapsableController,
  // Tooltip: CollapsableTooltip,
  Content: CollapsableContent,
});
// Collapsable.Controller = CollapsableController;
// // Collapsable.Tooltip = CollapsableTooltip;
// Collapsable.Content = CollapsableContent;

export default Collapsable;
